import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../client';
import { queryKeys } from '../queryKeys';

/** One registered browser/desktop device (a stored FCM token) for the user. */
export interface PushSubscription {
  id: string;
  userAgent: string | null;
  createdAt: string;
  lastSeenAt: string;
}

/** The current user's registered push devices, newest first. */
export function usePushSubscriptions() {
  return useQuery({
    queryKey: queryKeys.pushSubscriptions.list(),
    queryFn: async () => {
      const response = await apiClient.get<PushSubscription[]>(
        '/api/notifications/push-subscriptions',
      );
      return response.data;
    },
  });
}

/**
 * Register (or refresh) this browser's FCM token for the current user. The
 * backend upserts by token, so re-registering the same browser is idempotent.
 */
export function useRegisterPushSubscription() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (fcmToken: string) => {
      const response = await apiClient.post<PushSubscription>(
        '/api/notifications/push-subscriptions',
        { fcmToken, userAgent: navigator.userAgent },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.pushSubscriptions.list(),
      });
    },
  });
}

/** Result of a test-push request: whether push is enabled in this env, and how
 * many of the caller's devices it reached (0 = nothing registered). */
export interface TestPushResult {
  pushEnabled: boolean;
  deviceCount: number;
}

/**
 * Send a test desktop notification to the current user's own registered
 * devices. Returns diagnostics so the UI can explain a no-show (push disabled
 * in this env, or no device registered) instead of a silent "nothing happened".
 */
export function useSendTestPush() {
  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.post<TestPushResult>(
        '/api/notifications/push-subscriptions/test',
      );
      return response.data;
    },
  });
}

/** Remove one of the current user's registered devices. */
export function useRemovePushSubscription() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/api/notifications/push-subscriptions/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.pushSubscriptions.list(),
      });
    },
  });
}
